import {
  DomainEntity,
  EndpointPathParameter,
  EndpointQueryParameter,
  EntityDetail,
  EntityConfigEndpoint,
  Category,
  EntityConfigCustomOperation,
  ConfigEntityConfiguration,
  ConfigEntity,
  ConfigEntityDetail,
  ConfigEntityMeta,
} from '@web-config-app/core';

import {
  mockSimpleSchema,
  mockConfigEntitySchema,
  mockConfigEntitySchemaWithDomainEntityAttributes,
  mockConfigEntitySchemaWithDomainEntityAttributesAndRelationships,
} from './mock-schemas';

const mockEndpointPath = `v1/config-cactuses`;
const mockEndpointWithIdPath = `v1/config-cactuses/{cactusId}`;

const mockPathParams: EndpointPathParameter[] = [
  {
    name: 'cactusId',
    in: 'path',
    schema: {
      type: 'string',
    },
    required: true,
  },
];

const mockQueryParams: EndpointQueryParameter[] = [
  {
    name: 'arms[count]',
    in: 'query',
    schema: {
      type: 'string',
    },
    required: false,
  },
  {
    name: 'spikes[count]',
    in: 'query',
    schema: {
      type: 'string',
    },
    required: false,
  },
];

const includeParam: EndpointQueryParameter = {
  name: 'include',
  in: 'query',
  schema: {
    type: 'string',
  },
  required: false,
};

export const mockListEndpoint: EntityConfigEndpoint = {
  path: mockEndpointPath,
  method: 'GET',
  pathParameters: mockPathParams,
  queryParameters: mockQueryParams,
};

const mockGetEndpoint: EntityConfigEndpoint = {
  path: mockEndpointWithIdPath,
  method: 'GET',
  pathParameters: mockPathParams,
  queryParameters: [includeParam],
};

const mockCreateEndpoint: EntityConfigEndpoint = {
  path: mockEndpointPath,
  method: 'POST',
  pathParameters: mockPathParams,
  queryParameters: mockQueryParams,
};

const mockDuplicateEndpoint: EntityConfigEndpoint = {
  path: mockEndpointPath,
  method: 'POST',
};

const mockUpdateEndpoint: EntityConfigEndpoint = {
  path: mockEndpointWithIdPath,
  method: 'POST',
  pathParameters: mockPathParams,
  queryParameters: mockQueryParams,
};

export const mockCustomOperation: EntityConfigCustomOperation = {
  method: 'PATCH',
  label: {
    text: 'Purge',
  },
  loadingMessage: {
    text: 'Purge loading',
  },
  successMessage: {
    text: 'Purge success',
  },
  actionType: 'overflow',
  path: '/get-purge-now/{status}/cool/{id}',
  parameterMappings: {
    query: [
      {
        hello: 'yes',
      },
    ],
    path: [
      {
        id: 'id',
      },
      {
        status: 'attributes.entityMetadata.status',
      },
    ],
    requestBody: [
      {
        internalName: 'attributes.metaData.internalName',
      },
      {
        type: 'type',
      },
    ],
  },
};

/**
 * Domain entity mock (deprecated and legacy) for the non-CACT-BE backend code path (`isUsingConfigAppBackend: false`).
 * Use this when a test specifically exercises that branch (e.g. mutation hooks that build
 * request bodies without the config-entities envelope). Otherwise prefer `mockDomainEntity`
 * (CACT-BE, the canonical case).
 */
export const mockLegacyDomainEntity: DomainEntity = {
  id: 'cactuses',
  type: 'config-cactus',
  isUsingConfigAppBackend: false,
  category: Category.Default,
  labelKey: 'CACTUSES',
  schema: mockSimpleSchema,
  localizable: true,
  operations: ['list', 'create', 'get', 'update', 'clone'],
  endpoints: {
    list: mockListEndpoint,
    create: mockCreateEndpoint,
    get: mockGetEndpoint,
    update: mockUpdateEndpoint,
    clone: mockDuplicateEndpoint,
  },
  customOperations: {
    patch: mockCustomOperation,
  },
};

// (mockDomainEntity is defined below mockConfigEntityConfiguration so it can reference the
// global config-entities endpoints — the same way production CACT-BE entities get their
// endpoints pre-filtered from configEntity.endpoints by `buildEntityConfigs`.)

export const mockConfigEntityConfiguration: ConfigEntityConfiguration = {
  id: 'config-entities',
  category: 'core',
  endpoints: {
    list: {
      method: 'GET',
      queryParameters: [
        {
          name: 'page[size]',
          in: 'query',
          required: false,
          schema: {
            type: 'integer',
          },
          description:
            'The number of records to return in a result. Default is 10 per page.',
        },
        {
          name: 'page[offset]',
          in: 'query',
          required: false,
          schema: {
            type: 'integer',
          },
          description:
            'The Offset needed for pagination. Typically size * (page -1).',
        },
        {
          name: 'filter[domainEntityType]',
          in: 'query',
          required: false,
          schema: {
            type: 'string',
          },
        },
        {
          name: 'filter[domainEntityID]',
          in: 'query',
          required: false,
          schema: {
            type: 'string',
          },
        },
        {
          name: 'filter[status]',
          in: 'query',
          required: false,
          schema: {
            type: 'string',
          },
          description: 'entity status to filter by.',
        },
        {
          name: 'filter[query]',
          in: 'query',
          required: false,
          schema: {
            type: 'string',
          },
        },
      ],
      path: '/v1/config-entities',
    },
    create: {
      method: 'POST',
      path: '/v1/config-entities',
      requiredEntityMetadataFieldValues: {
        status: 'draft',
      },
    },
    update: {
      method: 'PATCH',
      pathParameters: [
        {
          name: 'entityId',
          in: 'path',
          required: true,
          schema: {
            type: 'string',
          },
          description: 'Entity Id',
        },
      ],
      path: '/v1/config-entities/{entityId}',
      requiredEntityMetadataFieldValues: {
        status: 'draft',
      },
    },
    publish: {
      method: 'PATCH',
      pathParameters: [
        {
          name: 'entityId',
          in: 'path',
          required: true,
          schema: {
            type: 'string',
          },
          description: 'Entity Id',
        },
      ],
      path: '/v1/config-entities/{entityId}',
      requiredEntityMetadataFieldValues: {
        status: 'published',
      },
    },
    republish: {
      method: 'PATCH',
      pathParameters: [
        {
          name: 'entityId',
          in: 'path',
          required: true,
          schema: {
            type: 'string',
          },
          description: 'Entity Id',
        },
      ],
      path: '/v1/config-entities/{entityId}',
      requiredEntityMetadataFieldValues: {
        status: 'published',
      },
    },
    archive: {
      method: 'PATCH',
      pathParameters: [
        {
          name: 'entityId',
          in: 'path',
          required: true,
          schema: {
            type: 'string',
          },
          description: 'Entity Id',
        },
      ],
      path: '/v1/config-entities/{entityId}',
      requiredEntityMetadataFieldValues: {
        status: 'archived',
      },
    },
    unpublish: {
      method: 'PATCH',
      pathParameters: [
        {
          name: 'entityId',
          in: 'path',
          required: true,
          schema: {
            type: 'string',
          },
          description: 'Entity Id',
        },
      ],
      path: '/v1/config-entities/{entityId}',
      requiredEntityMetadataFieldValues: {
        status: 'draft',
      },
    },
    unarchive: {
      method: 'PATCH',
      pathParameters: [
        {
          name: 'entityId',
          in: 'path',
          required: true,
          schema: {
            type: 'string',
          },
          description: 'Entity Id',
        },
      ],
      path: '/v1/config-entities/{entityId}',
      requiredEntityMetadataFieldValues: {
        status: 'draft',
      },
    },
    get: {
      method: 'GET',
      pathParameters: [
        {
          name: 'entityId',
          in: 'path',
          required: true,
          schema: {
            type: 'string',
          },
          description: 'Entity Id',
        },
      ],
      path: '/v1/config-entities/{entityId}',
    },
    deepClone: {
      method: 'POST',
      requiredEntityMetadataFieldValues: {
        status: 'draft',
        action: 'clone',
      },
      path: '/v1/config-entities',
      prefetch: {
        path: '/v1/config-entities/{entityId}',
        method: 'GET',
        pathParameters: [
          {
            name: 'entityId',
            in: 'path',
            required: true,
            schema: {
              type: 'string',
            },
            description: 'Entity Id',
          },
        ],
        queryParameters: [
          {
            name: 'include',
            in: 'query',
            required: true,
            schema: {
              type: 'string',
            },
            description: 'include',
          },
        ],
      },
    },
    clone: {
      method: 'POST',
      path: '/v1/config-entities',
      requiredEntityMetadataFieldValues: {
        status: 'draft',
      },
    },
    copyToEnvironment: {
      method: 'POST',
      path: '/v1/config-entities',
      requiredEntityMetadataFieldValues: {
        status: 'draft',
      },
    },
  },
  type: 'config-entity',
  localizable: true,
  schema: mockConfigEntitySchema,
};

/**
 * Canonical CACT-BE domain entity mock. Endpoints mirror reality: in production
 * `buildEntityConfigs` filters the global config-entities endpoints (`/v1/config-entities…`)
 * down to the entity's declared operations and attaches them here. The mock does the same.
 */
export const mockDomainEntity: DomainEntity = {
  id: mockLegacyDomainEntity.id,
  type: mockLegacyDomainEntity.type,
  category: mockLegacyDomainEntity.category,
  labelKey: mockLegacyDomainEntity.labelKey,
  schema: mockLegacyDomainEntity.schema,
  localizable: mockLegacyDomainEntity.localizable,
  operations: mockLegacyDomainEntity.operations,
  customOperations: mockLegacyDomainEntity.customOperations,
  endpoints: {
    list: mockConfigEntityConfiguration.endpoints.list,
    get: mockConfigEntityConfiguration.endpoints.get,
    create: mockConfigEntityConfiguration.endpoints.create,
    update: mockConfigEntityConfiguration.endpoints.update,
    clone: mockConfigEntityConfiguration.endpoints.clone,
    deepClone: mockConfigEntityConfiguration.endpoints.deepClone,
  },
};

export const mockConfigEntity: ConfigEntity = {
  ...mockConfigEntityConfiguration,
  domainEntity: mockDomainEntity,
};

export const mockConfigEntityWithDomainEntitySchemaAttributes = {
  ...mockConfigEntity,
  endpoints: mockDomainEntity.endpoints,
  schema: mockConfigEntitySchemaWithDomainEntityAttributes,
};

export const mockConfigEntityWithDomainEntitySchemaAttributesAndRelationships =
  {
    ...mockConfigEntity,
    endpoints: mockDomainEntity.endpoints,
    schema: mockConfigEntitySchemaWithDomainEntityAttributesAndRelationships,
  };

export const mockEntityDetail: EntityDetail = {
  id: '123456789012312455',
  type: 'campaign',
  attributes: {
    entityMetadata: {
      status: 'draft',
      name: 'Fun Campaign',
      readOnly: true,
      attachments: [
        {
          assetId: '123456789',
        },
      ],
    },
    metaData: {
      internalName: 'Fun Campaign',
    },
  },
};

export const mockEntityMeta: ConfigEntityMeta = {
  staticMessage: {
    status: 'info',
    description: 'This is a static message',
  },
};

export const mockConfigEntityDetail: ConfigEntityDetail = {
  ...mockEntityDetail,
  type: 'config-entity',
  attributes: {
    entityMetadata: {
      ...mockEntityDetail.attributes.entityMetadata,
      domainEntityType: 'config-cactus',
      name: 'Fun Campaign',
    },
    domainEntityAttributes: mockEntityDetail.attributes,
  },
} as const;
